'use client';

import Image from 'next/image';
import { usePathname } from 'next/navigation';
import Breadcrumb from '@/components/common/Breadcrumb';
import { useTranslation } from '@/hooks/useTranslation';
import { formatEventDateLong, titleCase } from '@/utils/formating';

// Cabecera de las sub-páginas del árbol público /event/*: sustituye al banner del microsite
// (MicrositeHero) replicando EXACTAMENTE la cabecera de la ficha pública (EventCard,
// section-product-showcase): mismo container, mismas clases, mismos iconos svg, mismo
// formato de fecha (formatEventDateLong compartido). Breadcrumb "RockTheSport > Evento >
// {página}" con "Evento" enlazando a la ficha. El banner con imagen/tema del organizador
// solo existe bajo /site/*.

// Segmento de ruta → clave i18n del label de la página. Segmentos no mapeados caen a
// title-case del propio segmento.
const SEGMENT_LABEL_KEYS: Record<string, string> = {
  information: 'eventPage.nav_info',
  classification: 'eventPage.nav_results',
  classifications: 'eventPage.nav_results',
  participants: 'eventPage.nav_participants',
  contact: 'eventPage.nav_contact',
  'my-registration': 'eventPage.nav_my_reg',
  bibmarket: 'eventPage.nav_bib_market',
  documents: 'eventPage.documents_title',
  teams: 'teams.title',
  gift: 'registration.gift_badge',
};

interface EventPublicHeaderProps {
  eventTitle: string;
  locationText?: string;
  // ISO de la fecha de inicio — se formatea con el weekday largo de la ficha
  // ("Sábado, 25 de julio de 2026").
  dateIso?: string;
  // Ciudad estructurada (event.location.city). Si falta, se recorta el locationText en la
  // primera coma como fallback (mounts que aún no la pasan).
  city?: string;
  // /{lang}/event/{alias} — el crumb "Evento" enlaza a la ficha pública.
  basePath: string;
}

// t() devuelve la propia clave cuando no existe la traducción — tratarlo como "sin label".
function translateOr(t: (key: string) => string, key: string, fallback: string): string {
  const value = t(key);
  return value && value !== key ? value : fallback;
}

export default function EventPublicHeader({ eventTitle, locationText, dateIso, city, basePath }: EventPublicHeaderProps) {
  const { t, lang } = useTranslation();
  // Primer segmento tras el basePath, con strip anclado al prefijo (replace() sin anclar
  // podría no matchear con encodings distintos y devolver el pathname entero → label "Es").
  const pathname = usePathname() ?? '';
  const rest = pathname.startsWith(basePath) ? pathname.slice(basePath.length) : '';
  const segment = rest.split('/').filter(Boolean)[0] ?? '';

  const fallbackLabel = segment ? titleCase(segment.replace(/-/g, ' ')) : '';
  const pageLabel = SEGMENT_LABEL_KEYS[segment]
    ? translateOr(t, SEGMENT_LABEL_KEYS[segment], fallbackLabel)
    : fallbackLabel;

  const items = [
    { label: translateOr(t, 'events.defaults.event_name', 'Evento'), href: basePath },
    ...(pageLabel ? [{ label: pageLabel }] : []),
  ];

  // Textos idénticos a la ficha: fecha con weekday y SOLO la ciudad ("VILLAVA", no
  // "VILLAVA, NAVARRA").
  const displayDate = dateIso ? formatEventDateLong(dateIso, lang) : '';
  const displayLocation = (city ?? locationText?.split(',')[0])?.trim();

  // Markup calcado de EventCard (Event Header). No cambiar clases/estructura sin cambiar
  // también la ficha — deben verse idénticos.
  return (
    <section className="py-8 pb-0">
      <div className="container">
        <Breadcrumb items={items} />

        <div className="mb-8 flex flex-col gap-2">
          <h1 className="text-xl sm:text-2xl font-semibold text-foreground leading-9 m-0 font-sans">
            {eventTitle}
          </h1>
          <div className="flex flex-row items-center gap-6 text-sm flex-wrap">
            {displayDate && (
              <div className="flex items-center gap-2">
                <Image src="/imagesEventCard/I15035_9747_15000_366574.svg" alt="Calendar" className="w-4 h-4 object-contain mr-2" width={16} height={16} />
                <span className="text-accent font-semibold">&nbsp;{displayDate}</span>
              </div>
            )}
            {displayLocation && (
              <div className="flex items-center gap-2" style={{ marginTop: '-5px' }}>
                <span className="flex items-center gap-1 font-normal" style={{ color: '#2ca1db' }}>
                  <Image src="/imagesEventCard/I15035_9747_14999_365661.svg" alt="Location" width={16} height={16} style={{ width: 16, height: 16, objectFit: 'contain', marginRight: 8 }} />
                  <span>{displayLocation.toUpperCase()}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
